import {
    getCustomStatisticViewFields,
    getInputInnerTypeMatch,
    getInputVariableOption,
    getViewCustomStatisticConfig,
    getViewVariableOption,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import { useCallback, useDeferredValue, useMemo } from 'react'

import { dataSourceAtomFamily } from '@/atoms/dataSource/state'
import { availableFieldBlocksAtom, blockHighlightAtom, lastPageOfStackAtom } from '@/atoms/page/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useCurrentCustomViewBlock } from '@/hooks/useCurrentCustomViewBlock'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

interface useGetColorVariableOptionsParams {
    customViewDsId?: string
}

export const useGetColorVariableOptions = ({ customViewDsId }: useGetColorVariableOptionsParams = {}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    const [pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.pageId || ''], [])
    )

    // 页面变量选项
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    // 用户变量选项
    const userOption = useGetUserRecordOptions()

    // 视图变量选项
    const customView = useCurrentCustomViewBlock()
    const extraFields = useMemo(
        () => getCustomStatisticViewFields(getViewCustomStatisticConfig(customView?.config), customView?.config.pointer),
        [customView]
    )
    const customViewSelfDataSource = useAtomData(dataSourceAtomFamily({ appId, envId, dsId: customViewDsId || '' }))
    const customViewSelfOption = useMemo(
        () =>
            customViewSelfDataSource
                ? getViewVariableOption({
                      dataSource: customViewSelfDataSource,
                      viewType: 'custom',
                      extraFields
                  })
                : undefined,
        [customViewSelfDataSource, extraFields]
    )
    const { customViewOption: customViewParentOption } = useVariableCustomViewOption()
    const viewOption = customViewSelfOption ?? customViewParentOption

    // 输入框变量选项 - 可选
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )
    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const inputOption = useMemo(() => {
        if (availableFieldBlocksInfo.length === 0) {
            return undefined
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFieldBlocksInfo, dataSourceList, handleMouseEnter, handleMouseLeave])

    return {
        innerTypes: getInputInnerTypeMatch('TEXT'),
        pageOptions,
        userOption,
        viewOptions: viewOption ? [viewOption] : undefined,
        inputOption
    }
}
